import React, { memo, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { ReportData } from "../../types/global.d";
import ReportDetailRow from "./ReportDetailRow";
import ReportCleanupDetails from "./ReportCleanupDetails";
import ReportSeparator from "./ReportSeparator";
import PollutionAmountBadge from "./PollutionAmountBadge";
import TrashTypesList from "./TrashTypesList";
import {
  getActionPlanLabel,
  getPollutionAreaLabel,
} from "../../utils/reportFormatting";

interface ReportDetailsContentProps {
  report: ReportData;
  onUserClick?: (
    nickname: string,
    avatar?: string,
    fullname?: string,
    vkUserId?: string
  ) => void;
  onMarkCleaned?: () => void;
  isAdmin?: boolean;
  isOwner?: boolean;
  onVerifyReport?: () => void;
  onDeclineReport?: () => void;
  onEditReport?: () => void;
  onVerifyCleaned?: () => void;
  onMarkNotCleaned?: () => void;
  onEditCleaned?: () => void;
  onCreateComplaint?: () => void;
  viewMode?: "report" | "cleaned";
}

const ReportDetailsContent: React.FC<ReportDetailsContentProps> = memo(
  ({
    report,
    onUserClick,
    onMarkCleaned,
    isAdmin,
    isOwner,
    onVerifyReport,
    onDeclineReport,
    onEditReport,
    onVerifyCleaned,
    onMarkNotCleaned,
    onEditCleaned,
    onCreateComplaint,
    viewMode = "report",
  }) => {
    const { t } = useTranslation();

    const reportDate = useMemo(() => {
      if (!report.date) {
        return report.cleaned?.date || t("report.noDate");
      }
      return report.date;
    }, [report.date, report.cleaned?.date, t]);

    const actionPlanLabel = useMemo(() => getActionPlanLabel(report), [report]);

    const pollutionAreaLabel = useMemo(
      () => getPollutionAreaLabel(report.area),
      [report.area],
    );

    const formattedCoordinates = useMemo(() => {
      return `${report.geoposition.latitude.toFixed(
        6,
      )}, ${report.geoposition.longitude.toFixed(6)}`;
    }, [report.geoposition.latitude, report.geoposition.longitude]);

    // Permission check for editing report
    const canEditReport = isAdmin || (isOwner && !report.verified);

    // Permission check for editing cleanup
    const canEditCleanup =
      report.cleaned && (isAdmin || (isOwner && !report.cleaned.verified));

    return (
      <div className="details-section">
        {/* Show report details when viewMode is "report" */}
        {viewMode === "report" && (
          <>
            <ReportSeparator canEdit={canEditReport} onEdit={onEditReport} />

            <ReportDetailRow label={t("report.date")} value={reportDate} />
            {/* 
            <ReportDetailRow
              label={t("report.location")}
              value={formattedLocation}
            /> */}

            <ReportDetailRow
              label={t("report.coordinates")}
              value={formattedCoordinates}
            />

            {report.user && (
              <ReportDetailRow
                label={t("report.reportedBy")}
                value={`@${report.user.nickname}`}
                clickable={!!onUserClick}
                onClick={
                  onUserClick
                    ? () =>
                        onUserClick(
                          report.user!.nickname,
                          report.user!.avatar,
                          report.user!.fullname,
                          report.user!.vkUserId,
                        )
                    : undefined
                }
              />
            )}

            {report.comment && (
              <ReportDetailRow
                label={t("report.comment")}
                value={report.comment}
              />
            )}

            {actionPlanLabel && (
              <ReportDetailRow
                label={t("actionPlan.actionPlan")}
                value={actionPlanLabel}
              />
            )}

            {typeof report.amount === "number" && (
              <div className="detail-row">
                <span className="detail-label">
                  {t("pollution.pollutionAmount")}:
                </span>
                <PollutionAmountBadge amount={report.amount} />
              </div>
            )}

            {pollutionAreaLabel && (
              <ReportDetailRow
                label={t("pollution.pollutionArea")}
                value={pollutionAreaLabel}
              />
            )}

            {report.trashTypes && report.trashTypes.length > 0 && (
              <div className="detail-row">
                <span className="detail-label">{t("trash.trashTypes")}:</span>
                <TrashTypesList trashTypes={report.trashTypes} />
              </div>
            )}

            {/* Admin features for unverified reports */}
            {isAdmin &&
              !report.verified &&
              onVerifyReport &&
              onDeclineReport &&
              onEditReport && (
                <div className="admin-features-section">
                  <div className="admin-label">{t("admin.adminFeatures")}</div>
                  <div className="admin-buttons-row">
                    <button
                      className="admin-action-btn decline-btn"
                      onClick={onDeclineReport}
                    >
                      {t("admin.declineReport")}
                    </button>
                    <button
                      className="admin-action-btn accept-btn"
                      onClick={onVerifyReport}
                    >
                      {t("admin.acceptReport")}
                    </button>
                    <button
                      className="admin-action-btn edit-btn"
                      onClick={onEditReport}
                    >
                      {t("admin.editReport")}
                    </button>
                  </div>
                </div>
              )}

            {/* Mark as Cleaned button for verified reports without cleanup */}
            {((!report.cleaned && onMarkCleaned) || onCreateComplaint) && (
              <div className="cleanup-actions-section">
                {!report.cleaned && onMarkCleaned && (
                  <button
                    className="mark-cleaned-button"
                    onClick={onMarkCleaned}
                  >
                    {t("cleanup.markAsCleaned")}
                  </button>
                )}
                {onCreateComplaint && (
                  <button
                    className="create-complaint-button"
                    onClick={onCreateComplaint}
                  >
                    <img
                      src={`${process.env.PUBLIC_URL}/images/write.png`}
                      alt=""
                      aria-hidden="true"
                      className="complaint-action-icon"
                    />
                    {t("complaintDraft.createButton")}
                  </button>
                )}
              </div>
            )}
          </>
        )}

        {/* Show cleanup details when viewMode is "cleaned" */}
        {viewMode === "cleaned" && report.cleaned && (
          <>
            <ReportCleanupDetails
              cleanedData={report.cleaned}
              showSectionHeading={true}
              onUserClick={onUserClick}
              canEdit={canEditCleanup}
              onEdit={onEditCleaned}
            />

            {/* Admin features for unverified cleanups */}
            {isAdmin &&
              !report.cleaned.verified &&
              onVerifyCleaned &&
              onMarkNotCleaned &&
              onEditCleaned && (
                <div className="admin-features-section">
                  <div className="admin-label">{t("admin.adminFeatures")}</div>
                  <div className="admin-buttons-row">
                    <button
                      className="admin-action-btn decline-btn"
                      onClick={onMarkNotCleaned}
                    >
                      {t("admin.notCleaned")}
                    </button>
                    <button
                      className="admin-action-btn accept-btn"
                      onClick={onVerifyCleaned}
                    >
                      {t("admin.accept")}
                    </button>
                    <button
                      className="admin-action-btn edit-btn"
                      onClick={onEditCleaned}
                    >
                      {t("admin.editReport")}
                    </button>
                  </div>
                </div>
              )}
          </>
        )}
      </div>
    );
  },
);

ReportDetailsContent.displayName = "ReportDetailsContent";

export default ReportDetailsContent;
